import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import queryString from 'querystring';

import { AppContext } from '@prisjakt/ui-app-context';
import { bemGenerator } from '@prisjakt/ui-base';
import ArrowIconLeft from 'mdi-react/ChevronLeftIcon';
import ArrowIconRight from 'mdi-react/ChevronRightIcon';
import ArrowIconLeftDouble from 'mdi-react/ChevronDoubleLeftIcon';

import './pagination.scss';

const styles = bemGenerator('pj-ui-pagination', [
  'paging',
  'paging-button',
  'paging-spacer',
  'paging-title',
  'more',
]);

const ITEMS_PER_PAGE_DEFAULT = 30;

function renderEmptyButton() {
  return <i className={styles['paging-spacer']} />;
}

class Pagination extends React.PureComponent {
  renderLinkButton(linkType, icon, onClickHandler) {
    const { location } = this.props.history;
    const qs = queryString.decode(location.search.substr(1));
    const { offsetParam, pageInfo } = this.props;
    const offset = parseInt(qs[offsetParam] || pageInfo.offset, 10) || 0;

    switch (linkType) {
      case 'reset': {
        qs[offsetParam] = Math.max(
          offset - (pageInfo.limit ? pageInfo.limit : ITEMS_PER_PAGE_DEFAULT),
          0
        );
        if (qs[offsetParam] === 0) {
          // If we will get to page=0 on previous, don't show another button
          return renderEmptyButton();
        }
        delete qs[offsetParam];
        break;
      }
      case 'previous': {
        if (!pageInfo.hasPrevious) {
          return renderEmptyButton(); // For layout reasons
        }

        qs[offsetParam] = Math.max(
          offset - (pageInfo.limit ? pageInfo.limit : ITEMS_PER_PAGE_DEFAULT),
          0
        );
        if (qs[offsetParam] === 0) {
          delete qs[offsetParam];
        }
        break;
      }
      case 'next': {
        if (!pageInfo.hasNext) {
          return renderEmptyButton(); // For layout reasons
        }

        qs[offsetParam] =
          offset + (pageInfo.limit ? pageInfo.limit : ITEMS_PER_PAGE_DEFAULT);
        break;
      }
      default:
        break;
    }

    const url =
      location.pathname +
      (Object.keys(qs).length > 0 ? `?${queryString.encode(qs)}` : '') +
      (location.hash || '');

    const pageNumber =
      (qs[offsetParam] || 0) / (pageInfo.limit || ITEMS_PER_PAGE_DEFAULT) + 1;
    return (
      <Link
        to={url}
        className={styles['paging-button']}
        onClick={() => onClickHandler(pageNumber)}
        aria-label={this.context.t('common', 'pagination.page-number', {
          number: pageNumber,
        })}
      >
        {icon}
      </Link>
    );
  }

  render() {
    const { pageInfo, onNext, onPrev, onReset } = this.props;
    const { t } = this.context;

    if (!pageInfo || (!pageInfo.hasNext && !pageInfo.hasPrevious)) {
      return null;
    }

    return (
      <div className={styles.block}>
        <div className={styles.paging}>
          {this.renderLinkButton('reset', <ArrowIconLeftDouble />, onReset)}
          {this.renderLinkButton('previous', <ArrowIconLeft />, onPrev)}
          <span className={styles['paging-title']}>
            {t('common', 'pagination.page-number', {
              number:
                Math.ceil(
                  (pageInfo.offset || 0) /
                    (pageInfo.limit || ITEMS_PER_PAGE_DEFAULT)
                ) + 1,
            })}
          </span>
          {this.renderLinkButton('next', <ArrowIconRight />, onNext)}
          {renderEmptyButton()}
        </div>
      </div>
    );
  }
}

Pagination.propTypes = {
  pageInfo: PropTypes.shape({
    offset: PropTypes.number,
    limit: PropTypes.number,
    hasNext: PropTypes.bool,
    hasPrevious: PropTypes.bool,
  }),
  offsetParam: PropTypes.string,
  onNext: PropTypes.func,
  onPrev: PropTypes.func,
  onReset: PropTypes.func,
  history: PropTypes.object.isRequired,
};

Pagination.defaultProps = {
  pageInfo: null,
  offsetParam: 'offset',
  onNext: () => {},
  onPrev: () => {},
  onReset: () => {},
};

export default withRouter(Pagination);

Pagination.contextType = AppContext;
